import React, { memo, useCallback, useEffect } from 'react';
import { useForm, useWatch, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import PropTypes from 'prop-types';

import Checkbox from 'components/Checkbox';
import Select from 'components/Select';
import Button from 'components/Button';
import PriceInput from 'components/PriceInput';

import { paymentFilter as TEXT } from 'locales/en';

import { AMOUNT_FILTERS, FRAUD } from './defaultFilters';
import { generateNewFilter } from './helpers';

const PaymentFilterForm = ({
  onSubmit,
  statusFilter,
  reasonFilter,
  defaultValues,
  schema,
  fieldNames,
}) => {
  const { register, control, handleSubmit, setValue } = useForm({
    defaultValues,
    resolver: yupResolver(schema),
  });

  const form = useWatch({
    control,
  });

  const handleFilterCheckboxChange = useCallback((filterName) => {
    if (filterName === 'status') {
      if (!form.statusCheckbox) {
        setValue('status', generateNewFilter(form.status, true));
      } else {
        setValue('status', generateNewFilter(form.status, false));
      }
    } else {
      if (!form.reasonCheckbox) {
        setValue('reason', generateNewFilter(form.reason, true));
      } else {
        setValue('reason', generateNewFilter(form.reason, false));
      }
    }
  }, [form, setValue]);

  const handleSubfiltersChange = useCallback((filterName) => {
    if (filterName === 'status') {
      if (!form.statusCheckbox) {
        setValue('statusCheckbox', true)
      }
    } else {
      if (!form.reasonCheckBox) {
        setValue('reasonCheckbox', true)
      }
    }
  }, [form, setValue]);

  useEffect(() => {
    if (form.reason) {
      const areReasonCheckboxesAllUnchecked = Object.values(form.reason).every(value => value === false);

      if (areReasonCheckboxesAllUnchecked && form.reasonCheckbox) {
        setValue('reasonCheckbox', false);
      }
    }

    if (form.status) {
      const areStatusCheckboxesAllUnchecked = Object.values(form.status).every(value => value === false);

      if (areStatusCheckboxesAllUnchecked && form.statusCheckbox) {
        setValue('statusCheckbox', false)
      }
    }
  }, [form, setValue])

  const { statusCheckbox, amountCheckbox, fraudCheckbox, reasonCheckbox } = fieldNames;

  const checkboxesFor = useCallback((checkbox, filters, headerText, filterName) => {
    return (
      <div className="px-5" data-testid={filterName}>
        <label htmlFor={checkbox}>
          <Checkbox
            name={checkbox}
            register={register}
            className="text-javelin-blue-dark"
            testId={`${filterName}-checkbox`}
            onChange={() => handleFilterCheckboxChange(filterName)}
          />
          <span className="label text-xs">{headerText}</span>
        </label>
        <ul
          data-testid={`${filterName}-filters`}
          className="transition-height mt-2"
        >
          {filters?.map((filter) => (
            <li key={filter.value}>
              <label htmlFor={`${filterName}.${filter.value}`}>
                <Checkbox
                  id={`${filterName}.${filter.value}`}
                  register={register}
                  name={`${filterName}.${filter.value}`}
                  className="text-javelin-blue-dark ml-4"
                  onChange={() => handleSubfiltersChange(filterName)}
                />
                <span className="label text-xs">{filter.label}</span>
              </label>
            </li>
          ))}
        </ul>
      </div>
    )
  }, [handleFilterCheckboxChange, handleSubfiltersChange, register])

  return (
    <form
      noValidate
      onSubmit={handleSubmit(onSubmit)}
      className="w-80 py-5 bg-white rounded shadow-md absolute top-12 right-0"
      data-testid="container"
    >
      {statusCheckbox && checkboxesFor(statusCheckbox, statusFilter, TEXT.status, 'status')}
      <hr className="horizontal-line" />
      {reasonCheckbox && checkboxesFor(reasonCheckbox, reasonFilter, TEXT.reason, 'reason')}
      {amountCheckbox !== undefined && (
        <>
          <hr className="horizontal-line" />
          <div className="px-5" data-testid="amount">
            <label htmlFor={amountCheckbox}>
              <Checkbox
                name={amountCheckbox}
                register={register}
                className="text-javelin-blue-dark"
                testId="amount-checkbox"
              />
              <span className="label text-xs">{TEXT.amount}</span>
            </label>
            <div
              className={`flex flex-row ml-4 transition-height ease-linear ${!!form[amountCheckbox] ? 'h-14' : 'invisible h-0'
                }`}
              data-testid="amount-filters"
            >
              <Controller
                name="amount_filter"
                control={control}
                render={({ field: { ref, value, ..._field } }) => (
                  <Select
                    {..._field}
                    options={AMOUNT_FILTERS}
                    className="mr-2 mt-3"
                    defaultValue={defaultValues.amount_filter}
                    isSearchable={false}
                  />
                )}
              />
              <PriceInput
                label={null}
                className={null}
                inputClassName="mt-3 w-28"
                placeholder="0.00"
                name="amount"
                control={control}
                thousandSeparator
                fixedDecimalScale
                decimalScale={2}
              />
            </div>
          </div>
        </>
      )}
      {fraudCheckbox !== undefined && (
        <>
          <hr className="horizontal-line" />
          <div className="px-5" data-testid="fraud">
            <label htmlFor={fraudCheckbox}>
              <Checkbox
                name={fraudCheckbox}
                register={register}
                className="text-javelin-blue-dark"
                testId="fraud-checkbox"
              />
              <span className="label text-xs">{TEXT.fraud}</span>
            </label>
            <div
              className={`flex flex-row justify-around ml-4 transition-height ease-linear ${!!form[fraudCheckbox] ? 'h-10 mt-3' : 'invisible h-0'
                }`}
              data-testid="fraud-filters"
            >
              <input
                {...register(FRAUD.fraud_score_min)}
                className="input input-full-border mr-2"
                placeholder="Min"
              />
              <input
                {...register(FRAUD.fraud_score_max)}
                className="input input-full-border"
                placeholder="Max"
              />
            </div>
          </div>
        </>
      )}
      <Button
        className="btn-blue block mt-5 mx-auto w-72"
        title={TEXT.apply}
        type="submit"
      />
    </form>
  );
};

PaymentFilterForm.propTypes = {
  onSubmit: PropTypes.func,
  statusFilter: PropTypes.arrayOf(PropTypes.shape({})),
  reasonFilter: PropTypes.arrayOf(PropTypes.shape({})),
  fieldNames: PropTypes.shape({
    statusCheckbox: PropTypes.string,
    amountCheckbox: PropTypes.string,
    fraudCheckbox: PropTypes.string,
    reasonCheckbox: PropTypes.string
  }),
};

export default memo(PaymentFilterForm);
